'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { getLegalPage, LegalPage } from '@/lib/dataStore';

interface LegalPublicPageProps {
  pageId: LegalPage['id'];
}

const PAGE_META: Record<LegalPage['id'], { title: string; backLabel: string }> = {
  agb: { title: 'Allgemeine Geschäftsbedingungen', backLabel: '← Zurück' },
  datenschutz: { title: 'Datenschutzerklärung', backLabel: '← Zurück' },
  widerruf: { title: 'Widerrufsrecht', backLabel: '← Zurück' },
};

export default function LegalPublicPage({ pageId }: LegalPublicPageProps) {
  const [content, setContent] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');

  useEffect(() => {
    const page = getLegalPage(pageId);
    setContent(page.content);
    setUpdatedAt(page.updatedAt);
  }, [pageId]);

  const meta = PAGE_META[pageId];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-50">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <Image
              src="/assets/images/Media_Rabbit_1_Logo_Zugeschnitten-1782856074259.png"
              alt="Media Rabbit GmbH"
              width={120}
              height={40}
              className="h-8 w-auto object-contain"
            />
          </Link>
          <Link
            href="/"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {meta.backLabel}
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-screen-md mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-3xl font-bold text-foreground mb-4">{meta.title}</h1>
        {updatedAt && (
          <p className="text-xs text-muted-foreground mb-10">
            Stand: {new Date(updatedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </p>
        )}
        <div
          className="prose prose-sm max-w-none text-foreground
            [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-foreground
            [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-foreground
            [&_p]:text-muted-foreground [&_p]:leading-relaxed [&_p]:mb-4
            [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:text-muted-foreground [&_ul]:mb-4
            [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:text-muted-foreground [&_ol]:mb-4
            [&_li]:mb-1 [&_strong]:text-foreground [&_a]:text-primary [&_a]:hover:underline
            [&_code]:bg-muted [&_code]:px-1 [&_code]:rounded [&_code]:text-xs"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-white mt-16 py-8">
        <div className="max-w-screen-md mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-muted-foreground">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link href="/impressum" className="hover:text-foreground transition-colors">Impressum</Link>
            <Link href="/agb" className="hover:text-foreground transition-colors">AGB</Link>
            <Link href="/datenschutz" className="hover:text-foreground transition-colors">Datenschutz</Link>
            <Link href="/widerruf" className="hover:text-foreground transition-colors">Widerrufsrecht</Link>
          </div>
          <p className="mt-3">© 2026 Media Rabbit GmbH</p>
        </div>
      </footer>
    </div>
  );
}
